import { useState } from 'react';
import { useMoneyDisplay } from '../utils/money';

interface UsePlaceholderFieldProps {
  initialValue: string;
  isPlaceholder?: boolean;
  onUpdate: (value: string) => void;
}

export const usePlaceholderField = ({
  initialValue,
  isPlaceholder,
  onUpdate
}: UsePlaceholderFieldProps) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);
  const placeholder = isPlaceholder ? value : '';

  const handleChange = (newValue: string) => {
    setValue(newValue);
    onUpdate(newValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (isPlaceholder) {
      setValue('');
      onUpdate('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  return {
    value,
    placeholder,
    isFocused,
    handleChange,
    handleFocus,
    handleBlur,
  };
};

interface UseNumberFieldProps extends UsePlaceholderFieldProps {
  formatOnBlur?: boolean;
  isPrice?: boolean;
}

export const useNumberField = ({
  initialValue,
  isPlaceholder,
  onUpdate,
  formatOnBlur = true,
  isPrice = false
}: UseNumberFieldProps) => {
  const { formatMoney, parseUserInput } = useMoneyDisplay();

  // Create base field with custom blur handler
  const baseField = usePlaceholderField({
    initialValue,
    isPlaceholder,
    onUpdate: (value) => {
      // Let the base field handle the raw value
      onUpdate(value);
    }
  });

  const handleBlur = (value: string) => {
    baseField.handleBlur();
    if (!formatOnBlur) return;

    if (value.trim() === '') {
      onUpdate(isPrice ? '0.00' : '0');
      return;
    }

    if (isPrice) {
      // Convert to universal format first, then format for display
      const universal = parseUserInput(value);
      onUpdate(formatMoney(universal));
    } else {
      // For non-price fields (like quantity), just parse and clean
      const number = parseFloat(value.replace(/[^\d.]/g, '')) || 0;
      onUpdate(String(number));
    }
  };

  return {
    ...baseField,
    handleBlur,
  };
};